import React, { useEffect, useMemo, useRef } from 'react';
import { Jodit } from 'jodit';
import type { IJodit } from 'jodit/types/types';
import { GOTRA_DESCRIPTION_MAX_LENGTH } from '../../types/gotra';
import { buildJoditFontFamilyList } from '../../utils/appFonts';
import '../../fonts/faces.css';
import 'jodit/es2021/jodit.min.css';
import './GotraDescriptionEditor.css';

export type GotraDescriptionEditorProps = {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  minHeight?: number;
};

const JODIT_FONT_LIST = Jodit.atom(buildJoditFontFamilyList());

const GotraDescriptionEditor: React.FC<GotraDescriptionEditorProps> = ({
  value = '',
  onChange,
  placeholder,
  maxLength = GOTRA_DESCRIPTION_MAX_LENGTH,
  minHeight = 220,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const editorRef = useRef<IJodit | null>(null);
  const onChangeRef = useRef(onChange);
  const lastHtmlRef = useRef(value ?? '');
  onChangeRef.current = onChange;

  const config = useMemo(() => {
    const defaultFontControl = Jodit.defaultOptions.controls.font;
    return {
      placeholder: placeholder ?? '',
      height: minHeight,
      zIndex: 11000,
      toolbarAdaptive: false,
      hidePoweredByJodit: true,
      saveSelectionOnBlur: true,
      limitChars: maxLength,
      showCharsCounter: true,
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      defaultActionOnPaste: 'insert_as_html' as const,
      controls: {
        font: {
          ...defaultFontControl,
          list: JODIT_FONT_LIST,
          childTemplate: (_editor: IJodit, key: string, label: string) => {
            const safeKey = String(key).replace(/"/g, '&quot;');
            let isAvailable = false;
            try {
              isAvailable =
                !safeKey.includes('dings') && document.fonts.check(`16px ${key}`);
            } catch {
              // ignore
            }
            return `<span data-style="${safeKey}" style="${
              isAvailable ? `font-family: ${key} !important;` : ''
            }">${label}</span>`;
          },
        },
      },
      buttons: [
        'font',
        'fontsize',
        '|',
        'bold',
        'italic',
        'underline',
        '|',
        'ul',
        'ol',
        '|',
        'link',
        '|',
        'align',
        '|',
        'undo',
        'redo',
        '|',
        'source',
        'fullsize',
      ],
    };
  }, [placeholder, minHeight, maxLength]);

  useEffect(() => {
    const element = textareaRef.current;
    if (!element) return;

    const editor = Jodit.make(element, config);
    editorRef.current = editor;
    const initial = value ?? '';
    editor.value = initial;
    lastHtmlRef.current = initial;

    const handleChange = () => {
      const html = editor.value;
      lastHtmlRef.current = html;
      onChangeRef.current?.(html);
    };
    editor.events.on('change', handleChange);

    return () => {
      editor.events.off('change', handleChange);
      editor.destruct();
      editorRef.current = null;
    };
  }, [config]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const next = value ?? '';
    if (next === lastHtmlRef.current) return;
    lastHtmlRef.current = next;
    if (editor.value !== next) {
      editor.value = next;
    }
  }, [value]);

  return <textarea ref={textareaRef} />;
};

export default GotraDescriptionEditor;
